import { useTheme } from '@mui/material/styles';
import { CSSProperties, RefObject, useCallback, useLayoutEffect, useRef } from 'react';

export interface IUseAutoSizeTextOptions {
  /**
   * Optional selector for element which serves as width boundary
   * If omitted, parent element is used
   */
  containerSelector?: string;
  /**
   * Max possibe font-size
   */
  maxFontSize?: number;
  /**
   * Smallest possible font-size
   * defaults to MUI theme typography
   */
  minFontSize?: number;
  /**
   * Recalculation triggerer, i.e. rendered text value
   */
  resizeTrigger?: unknown;
  /**
   * Selector used to find element within for style to be applied to
   * i.e. input, div.class-name > input...
   */
  targetSelector?: string;
}

export interface IUseAutoSizeTextResult<TElement extends HTMLElement> {
  /**
   * Styles that keeps text resized and prevent overflow
   */
  autoSizeTextStyle: CSSProperties;
  /**
   * Ref to be attached to the root element or component
   */
  ref: RefObject<TElement | null>;
  /**
   * Recalculate and apply the fitted font size if needed
   */
  resizeText: () => void;
}

const AUTO_SIZE_TEXT_STYLE: CSSProperties = {
  display: 'block',
  marginInline: 0,
  maxWidth: '100%',
  overflow: 'visible',
  whiteSpace: 'nowrap',
};

/**
 * Automatically resize font-size within desired boundaries of container element
 *
 * Attach returned `ref` to element itself, or to a component root and use
 * `targetSelector` to select nested text element
 */
export function useAutoSizeText<TElement extends HTMLElement = HTMLElement>({
  containerSelector,
  maxFontSize = 60,
  minFontSize,
  resizeTrigger,
  targetSelector,
}: IUseAutoSizeTextOptions = {}): IUseAutoSizeTextResult<TElement> {
  const theme = useTheme();
  const ref = useRef<TElement>(null);
  // defaults to theme font size
  const resolvedMinFontSize = minFontSize ?? theme.typography.fontSize;

  const getElements = useCallback(() => {
    const root = ref.current;

    if (!root) {
      return {};
    }

    const target = targetSelector ? root.querySelector<HTMLElement>(targetSelector) : root;
    const container = containerSelector
      ? root.querySelector<HTMLElement>(containerSelector)
      : (target?.parentElement ?? undefined);

    return {
      container,
      target,
    };
  }, [containerSelector, targetSelector]);

  const resizeText = useCallback(() => {
    const { container, target } = getElements();

    if (!container || !target) {
      return;
    }

    let min = resolvedMinFontSize;
    let max = maxFontSize;
    let fittedSize = resolvedMinFontSize;

    while (min <= max) {
      const size = Math.floor((min + max) / 2);

      target.style.fontSize = `${size}px`;

      if (target.scrollWidth <= container.clientWidth) {
        fittedSize = size;
        min = size + 1;
      } else {
        max = size - 1;
      }
    }

    target.style.fontSize = `${fittedSize}px`;
  }, [getElements, maxFontSize, resolvedMinFontSize]);

  useLayoutEffect(() => {
    const { container } = getElements();

    if (!container) {
      return undefined;
    }

    resizeText();

    const observer = new ResizeObserver(resizeText);
    observer.observe(container);

    document.fonts?.ready.then(resizeText).catch(() => undefined);

    return () => observer.disconnect();
  }, [getElements, resizeText, resizeTrigger]);

  return {
    autoSizeTextStyle: AUTO_SIZE_TEXT_STYLE,
    ref,
    resizeText,
  };
}
